import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import MagneticButton from "../components/MagneticButton";
import { submitInquiry } from "../lib/api";
import { submitToNetlify } from "../lib/netlify";
import { toast } from "sonner";

const EXPERIENCES = [
  {
    name: "Her/His/Their Night In",
    base: 450,
    extra: 25,
    summary: "$450 up to 6 guests · $25/each additional guest",
    description: "We will make an appetizer, dessert, and a cocktail/mocktail.",
  },
  {
    name: "Sprinkles Birthday Class (Youth)",
    base: 450,
    extra: 25,
    summary: "$450 up to 6 guests · $25/each additional guest",
    description: "Kids make a miniature birthday cake with a sprinkle center. A fun mocktail is also included.",
  },
  {
    name: "Dessert & Drinks",
    base: 500,
    extra: 30,
    summary: "$500 up to 6 guests · $30/each additional guest",
    description: "Seasonal desserts and a minimum of two craft cocktails.",
  },
  {
    name: "Pasta & Wine",
    base: 650,
    extra: 40,
    summary: "$650 up to 6 guests · $40/each additional guest",
    description: "Cut your own pasta and make a cocktail/mocktail.",
  },
  {
    name: "Premium Pasta Dinner",
    base: 1000,
    extra: 60,
    summary: "$1,000 up to 6 guests · $60/each additional guest",
    description: "Drinks and appetizers, then we cut and stuff pasta. Finish with a dessert bar and digestif.",
  },
  {
    name: "Vegan Favorites",
    base: 500,
    extra: 30,
    summary: "$500 up to 6 guests · $30/each additional guest",
    description: "A vegan appetizer, a seasonal main course, and a craft cocktail.",
  },
];

const STEPS = ["You", "The Gathering", "The Menu", "Final Notes"];

export default function PrivateBookings() {
  const [step, setStep] = useState(0);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    guest_count: 6,
    preferred_date: "",
    event_type: "Pasta & Wine",
    cuisine_preferences: "",
    dietary_restrictions: "",
    budget_range: "$500 - $1,500",
    notes: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const estimatedTotal = () => {
    const exp = EXPERIENCES.find((x) => x.name === form.event_type) || EXPERIENCES[3];
    const guests = Number(form.guest_count) || 0;
    const extraGuests = Math.max(0, guests - 6);
    return exp.base + exp.extra * extraGuests;
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.preferred_date) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);

    const payload = { ...form, guest_count: Number(form.guest_count) };

    const [netlifyResult, backendResult] = await Promise.allSettled([
      submitToNetlify("private-bookings", payload),
      submitInquiry(payload),
    ]);
    const netlifyOk = netlifyResult.status === "fulfilled" && netlifyResult.value === true;
    const backendOk = backendResult.status === "fulfilled";

    if (netlifyOk || backendOk) {
      setSuccess(true);
    } else {
      toast.error("Something went wrong. Try again or email salumitucson@gmail.com");
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>Private Bookings — Custom Culinary Experiences in Tucson | Salumi Tucson</title>
        <meta name="description" content="Book a private cooking class, cocktail workshop, dinner party or charcuterie experience in Tucson. Birthdays, date nights, girls nights, and intimate celebrations curated by Salumi Tucson." />
        <link rel="canonical" href="https://salumitucson.com/private-bookings" />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-espresso text-ivory pt-40 md:pt-56 pb-28 overflow-hidden" data-testid="private-bookings-hero">
        <video
          src="/media/videos/salumi-11.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-espresso/40 to-espresso" />
        <div className="absolute inset-0 light-leak opacity-50" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.3em] text-ivory/60 mb-6">Custom culinary experiences</p>
          </Reveal>
          <motion.h1
            initial={{ opacity: 0, y: 40, filter: "blur(14px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-6xl md:text-8xl lg:text-9xl tracking-editorial leading-[0.88] max-w-5xl"
          >
            Host the kind of event<br />
            <em className="not-italic text-terracotta">no one wants to leave.</em>
          </motion.h1>
          <Reveal delay={0.4}>
            <p className="mt-12 max-w-3xl text-lg text-ivory/75 leading-relaxed">
              Private classes can be catered to your theme and the tastes and interests of your guests. If you're undecided we are happy to help you find the perfect course. Creating something new for each group is part of the fun. A private class generally includes a cocktail, appetizer or light bites, and a heartier dish and/or a dessert depending on the interests of the group and budget for your event. There is a broad range of possibilities for the menu your group will make together (e.g. specialty desserts, fresh cut pasta, custom cocktails, charcuterie, brunch favorites, liqueur crafting, and more). Pricing begins at $400 for the first 6 guests, and an additional $30/person for each additional guest. Private classes can be hosted in your home or another venue. Youth classes are also offered. We welcome your inquiries.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Experiences strip */}
      <section className="bg-ivory text-espresso py-20" data-testid="experiences-list">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <p className="text-[11px] uppercase tracking-[0.3em] text-olive mb-10">Experiences we host</p>
          <ul className="grid md:grid-cols-2 gap-y-10 gap-x-12">
            {EXPERIENCES.map((x, i) => (
              <motion.li
                key={x.name}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="border-b border-espresso/12 pb-6"
              >
                <h3 className="font-serif text-2xl md:text-3xl tracking-editorial mb-2">{x.name}</h3>
                <p className="text-[11px] uppercase tracking-[0.22em] text-espresso/55 mb-3">{x.summary}</p>
                <p className="text-base text-espresso/75 leading-relaxed">{x.description}</p>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* Form */}
      <section className="bg-sand text-espresso py-32" data-testid="inquiry-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {success ? (
            <SuccessState onReset={() => { setSuccess(false); setStep(0); }} />
          ) : (
            <>
              <Reveal>
                <p className="text-[11px] uppercase tracking-[0.3em] text-olive mb-4">Step {step + 1} of {STEPS.length}</p>
                <h2 id="contact" className="font-serif text-4xl md:text-6xl tracking-editorial leading-[0.95] mb-12 scroll-mt-32">
                  {STEPS[step]}.
                </h2>
              </Reveal>

              <div className="flex gap-2 mb-14" data-testid="step-progress">
                {STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={`h-[2px] flex-1 transition-colors duration-500 ${
                      i <= step ? "bg-espresso" : "bg-espresso/15"
                    }`}
                  />
                ))}
              </div>

              <form
                onSubmit={submit}
                name="private-bookings"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                data-testid="private-bookings-form"
                className="space-y-10"
              >
                <input type="hidden" name="form-name" value="private-bookings" />
                <input type="hidden" name="bot-field" />
                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div
                      key="step-0"
                      initial={{ opacity: 0, x: 20, filter: "blur(6px)" }}
                      animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0, x: -20, filter: "blur(6px)" }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="space-y-10"
                    >
                      <Field label="What's your name?" required>
                        <Input value={form.name} name="name" onChange={(v) => set("name", v)} placeholder="First and last" testId="form-name" />
                      </Field>
                      <Field label="Where should we write you?" required>
                        <Input type="email" value={form.email} name="email" onChange={(v) => set("email", v)} placeholder="your@email.com" testId="form-email" />
                      </Field>
                      <Field label="Phone (optional)">
                        <Input value={form.phone} name="phone" onChange={(v) => set("phone", v)} placeholder="(520) 555-0123" testId="form-phone" />
                      </Field>
                    </motion.div>
                  )}
                  {step === 1 && (
                    <motion.div
                      key="step-1"
                      initial={{ opacity: 0, x: 20, filter: "blur(6px)" }}
                      animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0, x: -20, filter: "blur(6px)" }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="space-y-10"
                    >
                      <Field label="What kind of event?">
                        <select
                          value={form.event_type}
                          onChange={(e) => set("event_type", e.target.value)}
                          data-testid="form-event-type"
                          className="w-full bg-transparent border-b border-espresso/30 text-2xl py-3 focus:outline-none focus:border-espresso appearance-none cursor-pointer"
                        >
                          {EXPERIENCES.map((x) => <option key={x.name}>{x.name}</option>)}
                        </select>
                      </Field>
                      <Field label="How many guests?">
                        <Input type="number" min="2" max="40" value={form.guest_count} name="guest_count" onChange={(v) => set("guest_count", v)} testId="form-guest-count" />
                      </Field>
                      <Field label="A date in mind?" required>
                        <Input type="date" value={form.preferred_date} name="preferred_date" onChange={(v) => set("preferred_date", v)} testId="form-date" />
                      </Field>
                    </motion.div>
                  )}
                  {step === 2 && (
                    <motion.div
                      key="step-2"
                      initial={{ opacity: 0, x: 20, filter: "blur(6px)" }}
                      animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0, x: -20, filter: "blur(6px)" }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="space-y-10"
                    >
                      <Field label="Cuisine preferences">
                        <Input value={form.cuisine_preferences} name="cuisine_preferences" onChange={(v) => set("cuisine_preferences", v)} placeholder="Italian, Mediterranean, surprise us…" testId="form-cuisine" />
                      </Field>
                      <Field label="Dietary restrictions">
                        <Input value={form.dietary_restrictions} name="dietary_restrictions" onChange={(v) => set("dietary_restrictions", v)} placeholder="Anything we should know" testId="form-dietary" />
                      </Field>
                    </motion.div>
                  )}
                  {step === 3 && (
                    <motion.div
                      key="step-3"
                      initial={{ opacity: 0, x: 20, filter: "blur(6px)" }}
                      animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0, x: -20, filter: "blur(6px)" }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="space-y-10"
                    >
                      <Field label="Budget range">
                        <select
                          value={form.budget_range}
                          onChange={(e) => set("budget_range", e.target.value)}
                          data-testid="form-budget"
                          className="w-full bg-transparent border-b border-espresso/30 text-2xl py-3 focus:outline-none focus:border-espresso appearance-none cursor-pointer"
                        >
                          <option>Under $500</option>
                          <option>$500 - $1,500</option>
                          <option>$1,500 - $3,000</option>
                          <option>$3,000+</option>
                        </select>
                      </Field>
                      <Field label="Anything else to share?">
                        <textarea
                          value={form.notes}
                          name="notes"
                          onChange={(e) => set("notes", e.target.value)}
                          rows={4}
                          data-testid="form-notes"
                          placeholder="Occasion, vibe, dream menu, surprise reveal…"
                          className="w-full bg-transparent border-b border-espresso/30 text-xl py-3 focus:outline-none focus:border-espresso resize-none"
                        />
                      </Field>

                      <div className="border-t border-espresso/15 pt-6 flex items-end justify-between">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.3em] text-olive mb-2">Estimated total</p>
                          <p className="font-serif text-5xl tracking-editorial">${estimatedTotal().toLocaleString()}</p>
                        </div>
                        <p className="text-xs text-espresso/50 max-w-[14rem] text-right">
                          Final pricing confirmed after we chat. Includes ingredients, instruction, and warm hospitality.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-center justify-between pt-8">
                  <button
                    type="button"
                    onClick={prev}
                    disabled={step === 0}
                    data-testid="form-prev"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-espresso/60 hover:text-espresso disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft strokeWidth={1.25} className="size-4" /> Back
                  </button>
                  {step < STEPS.length - 1 ? (
                    <button
                      type="button"
                      onClick={next}
                      data-testid="form-next"
                      className="inline-flex items-center gap-3 bg-espresso text-ivory px-8 py-4 text-[11px] uppercase tracking-[0.22em] hover:bg-terracotta transition-colors duration-500"
                    >
                      Continue <ArrowRight strokeWidth={1.25} className="size-4" />
                    </button>
                  ) : (
                    <MagneticButton variant="terracotta" type="submit" disabled={loading} data-testid="form-submit">
                      {loading ? "Sending…" : "Send inquiry"}
                      <ArrowRight strokeWidth={1.25} className="size-4" />
                    </MagneticButton>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.3em] text-olive mb-3">
        {label}{required && <span className="text-terracotta"> *</span>}
      </label>
      {children}
    </div>
  );
}

function Input({ value, onChange, type = "text", placeholder, testId, name, min, max }) {
  return (
    <input
      type={type}
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      max={max}
      data-testid={testId}
      className="w-full bg-transparent border-b border-espresso/30 text-2xl py-3 placeholder-espresso/30 focus:outline-none focus:border-espresso"
    />
  );
}

function SuccessState({ onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className="text-center py-20"
      data-testid="inquiry-success"
    >
      <div className="inline-flex items-center justify-center size-16 border border-terracotta text-terracotta mb-8">
        <Check strokeWidth={1.25} className="size-6" />
      </div>
      <h3 className="font-serif text-5xl md:text-7xl tracking-editorial leading-[0.95] mb-6">
        We received your <em className="not-italic text-terracotta">note</em>.
      </h3>
      <p className="text-lg text-espresso/70 max-w-xl mx-auto leading-relaxed">
        Sit by the window with a cup of something. We'll write back within a day with thoughts, options, and the next step.
      </p>
      <button onClick={onReset} className="mt-12 text-[11px] uppercase tracking-[0.22em] editorial-underline">
        Send another inquiry
      </button>
    </motion.div>
  );
}
